<template>
	<div v-show="display" class="vendor-contact">
		<Flyout
			title="Contacts"
			subHeading="Drag and drop to change primary contact"
			:display="display"
			@show="closeVendorContactsEditFlyout"
			:novalidate="'novalidate'"
		>
			<template #flyout-content>
				<Spinner v-if="loading" :processor="{ loading: true }" />
				<div
					class="vendor-contact"
					v-show="!loading"
					data-test="flyoutContent"
				>
					<!-- SUPPORT CATEGORY -->

					<div class="vendor-contact__support">
						<div
							:class="[
								'vendor-contact__text',
								supportNotInEditMode && 'is-disabled'
							]"
							data-test="headerSupportContact"
						>
							Support
						</div>
						<DraggableList
							v-if="contactsSupport.length"
							class="vendor-contact__draggable-list"
							groupName="contacts-support"
							v-model="contactsSupport"
							@change="() => setNewPrimary(contactsSupport, 2)"
						>
							<DraggableListItem
								v-for="(contact, index) in contactsSupport"
								:key="index"
								:isDraggableMode="
									editMode || hasOnlyReadPermission
								"
								class="vendor-contact__draggable-list-item"
								:isPrimary="contact.primary"
								:disabled="
									oneElementInEditMode &&
										contactsSupportEditId !== contact.id
								"
							>
								<template #header>
									<span
										v-if="contact.primary"
										class="vendor-contact__primary"
									>
										Primary
									</span>
								</template>

								<template #content>
									<VendorContactItem
										v-model="contactsSupport[index]"
										@edit="
											() => supportEditMode(contact.id)
										"
										@save="
											() =>
												saveAction(
													contactsSupport[index],
													index === 0,
													2
												)
										"
										@cancel="() => cancelAction(contact, 2)"
										@delete="
											() =>
												deleteAction(
													contactsSupport,
													index,
													2
												)
										"
										:editMode="
											contactsSupportEditId === contact.id
										"
										:disabledDelete="
											contactsSupport.length === 1
										"
									/>
								</template>
							</DraggableListItem>
						</DraggableList>
						<Button
							labelText="Add Contact"
							:btnClick="() => addContact(2)"
							iconClassName="icon-more"
							:iconBeforeLabel="true"
							className="btn--has-icon vendor-contact__add"
							v-if="hasCreatePermission"
							:disabled="editMode"
							ref="addBtnRef"
						/>
					</div>

					<!-- BUSINESS DEV CATEGORY -->

					<div class="vendor-contact__business">
						<div
							:class="[
								'vendor-contact__text',
								businessNotInEditMode && 'is-disabled'
							]"
							data-test="headerBusinessContact"
						>
							Business Development
						</div>
						<DraggableList
							v-if="contactsBusiness.length"
							class="vendor-contact__draggable-list"
							groupName="contacts-business"
							v-model="contactsBusiness"
							@change="() => setNewPrimary(contactsBusiness, 1)"
						>
							<DraggableListItem
								v-for="(contact, index) in contactsBusiness"
								:key="index"
								:isDraggableMode="
									editMode || hasOnlyReadPermission
								"
								class="vendor-contact__draggable-list-item"
								:isPrimary="contact.primary"
								:disabled="
									oneElementInEditMode &&
										contactsBusinessEditId !== contact.id
								"
							>
								<template #header>
									<span
										v-if="contact.primary"
										class="vendor-contact__primary"
									>
										Primary
									</span>
								</template>

								<template #content>
									<VendorContactItem
										v-model="contactsBusiness[index]"
										@edit="
											() => businessEditMode(contact.id)
										"
										@save="
											() =>
												saveAction(
													contactsBusiness[index],
													index === 0,
													1
												)
										"
										@cancel="() => cancelAction(contact, 1)"
										@delete="
											() =>
												deleteAction(
													contactsBusiness,
													index,
													1
												)
										"
										:editMode="
											contactsBusinessEditId ===
												contact.id
										"
									/>
								</template>
							</DraggableListItem>
						</DraggableList>
						<div class="vendor-contact__help-text" v-else>
							No Business Development contacts defined.
						</div>
						<Button
							labelText="Add Contact"
							:btnClick="() => addContact(1)"
							iconClassName="icon-more"
							:iconBeforeLabel="true"
							className="btn--has-icon vendor-contact__add"
							v-if="hasCreatePermission"
							:disabled="editMode"
							ref="addBtnRef"
						/>
					</div>

					<!-- INTERNAL CATEGORY -->

					<div class="vendor-contact__internal">
						<div
							:class="[
								'vendor-contact__text',
								internalNotInEditMode && 'is-disabled'
							]"
							data-test="headerInternalContact"
						>
							Internal
						</div>
						<DraggableList
							v-if="contactsInternal.length"
							class="vendor-contact__draggable-list"
							groupName="contacts-internal"
							v-model="contactsInternal"
							@change="() => setNewPrimary(contactsInternal, 3)"
						>
							<DraggableListItem
								v-for="(contact, index) in contactsInternal"
								:key="index"
								:isDraggableMode="
									editMode || hasOnlyReadPermission
								"
								class="vendor-contact__draggable-list-item"
								:isPrimary="contact.primary"
								:disabled="
									oneElementInEditMode &&
										contactsInternalEditId !== contact.id
								"
							>
								<template #header>
									<span
										v-if="contact.primary"
										class="vendor-contact__primary"
									>
										Primary
									</span>
								</template>

								<template #content>
									<VendorContactItem
										v-model="contactsInternal[index]"
										@edit="
											() => internalEditMode(contact.id)
										"
										@save="
											() =>
												saveAction(
													contactsInternal[index],
													index === 0,
													3
												)
										"
										@cancel="() => cancelAction(contact, 3)"
										@delete="
											() =>
												deleteAction(
													contactsInternal,
													index,
													3
												)
										"
										:editMode="
											contactsInternalEditId ===
												contact.id
										"
									/>
								</template>
							</DraggableListItem>
						</DraggableList>
						<div class="vendor-contact__help-text" v-else>
							No Internal contacts defined.
						</div>
						<Button
							labelText="Add Contact"
							:btnClick="() => addContact(3)"
							iconClassName="icon-more"
							:iconBeforeLabel="true"
							className="btn--has-icon vendor-contact__add"
							v-if="hasCreatePermission"
							:disabled="editMode"
							ref="addBtnRef"
						/>
					</div>

					<!-- TECHNICAL CATEGORY -->

					<div class="vendor-contact__internal">
						<div
							:class="[
								'vendor-contact__text',
								technicalNotInEditMode && 'is-disabled'
							]"
							data-test="headerTechnicalContact"
						>
							Technical
						</div>
						<DraggableList
							v-if="contactsTechnical.length"
							class="vendor-contact__draggable-list"
							groupName="contacts-technical"
							v-model="contactsTechnical"
							@change="() => setNewPrimary(contactsTechnical, 4)"
						>
							<DraggableListItem
								v-for="(contact, index) in contactsTechnical"
								:key="index"
								:isDraggableMode="
									editMode || hasOnlyReadPermission
								"
								class="vendor-contact__draggable-list-item"
								:isPrimary="contact.primary"
								:disabled="
									oneElementInEditMode &&
										contactsTechnicalEditId !== contact.id
								"
							>
								<template #header>
									<span
										v-if="contact.primary"
										class="vendor-contact__primary"
									>
										Primary
									</span>
								</template>

								<template #content>
									<VendorContactItem
										v-model="contactsTechnical[index]"
										@edit="
											() => technicalEditMode(contact.id)
										"
										@save="
											() =>
												saveAction(
													contactsTechnical[index],
													index === 0,
													4
												)
										"
										@cancel="() => cancelAction(contact, 4)"
										@delete="
											() =>
												deleteAction(
													contactsTechnical,
													index,
													4
												)
										"
										:editMode="
											contactsTechnicalEditId ===
												contact.id
										"
									/>
								</template>
							</DraggableListItem>
						</DraggableList>
						<div class="vendor-contact__help-text" v-else>
							No Technical contacts defined.
						</div>
						<Button
							labelText="Add Contact"
							:btnClick="() => addContact(4)"
							iconClassName="icon-more"
							:iconBeforeLabel="true"
							className="btn--has-icon vendor-contact__add"
							v-if="hasCreatePermission"
							:disabled="editMode"
							ref="addBtnRef"
						/>
					</div>
				</div>
			</template>
		</Flyout>

		<!-- DEV NOTES: Please review these two modals behavior one is for delete contact and the other when user click on "Cancel" button without save changes -->
		<Modals
			v-model="showConfirmationModal"
			:title="'Are you sure you want to delete this contact?'"
			:type="'confirmation'"
			labelConfirmButton="Yes, delete contact"
			labelCancelButton="Cancel"
			ref="confirmationModal"
		/>

		<Modals
			:type="'confirmation'"
			:title="'Would you like to save changes before exiting?'"
			:confirmationContent="'Unsaved changes will be lost'"
		/>
	</div>
</template>

<script>
import Flyout from '@/components/shells/flyout/flyout';
import Button from '@/components/forms/button/button';
import Modals from '@/components/shells/modal/modals';
import DraggableList from '@/components/shells/draggable-list/draggable-list';
import DraggableListItem from '@/components/shells/draggable-list/draggable-list-item';
import VendorContactItem from '@/sections/vendors/settings/vendor-contact-item';
import PaginationPage from '@/mixins/infiniteScroll/page';
import Spinner from '@/components/visual/spinner/spinner';
import { mapActions, mapGetters } from 'vuex';
import { hasPermissionByGroupName } from '@/utils/function';
import { integratorsContactTypes } from './integratorsContactTypes';
import _ from 'lodash';

export default {
	name: 'VendorContactFlyout',
	mixins: [PaginationPage],
	components: {
		Flyout,
		Button,
		Modals,
		VendorContactItem,
		DraggableList,
		DraggableListItem,
		Spinner
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		vendorId: {
			type: [Number, String],
			required: true
		},
		display: {
			type: Boolean,
			default: false
		},
		validateBeforeClose: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			vendorData: {},
			contactsBusiness: [],
			contactsSupport: [],
			contactsInternal: [],
			contactsTechnical: [],
			contactsBusinessEditId: -1,
			contactsSupportEditId: -1,
			contactsInternalEditId: -1,
			contactsTechnicalEditId: -1,
			editMode: false,
			showConfirmationModal: false,
			deleteContactObj: '',
			action: '',
			primaryContactId: 0,
			loading: false
		};
	},
	computed: {
		...mapGetters('integrators', ['contactsVendor']),
		hasCreatePermission() {
			return this.validatePermission('vendors.create');
		},
		hasOnlyReadPermission() {
			const user = this.$store.getters['oidcStore/oidcUser'];
			return hasPermissionByGroupName('vendors', 'read', user);
		},
		oneElementInEditMode() {
			if (
				this.contactsBusinessEditId !== -1 ||
				this.contactsSupportEditId !== -1 ||
				this.contactsInternalEditId !== -1 ||
				this.contactsTechnicalEditId !== -1
			) {
				return true;
			}
			return false;
		},
		businessNotInEditMode() {
			return this.editMode && this.contactsBusinessEditId === -1;
		},
		supportNotInEditMode() {
			return this.editMode && this.contactsSupportEditId === -1;
		},
		internalNotInEditMode() {
			return this.editMode && this.contactsInternalEditId === -1;
		},
		technicalNotInEditMode() {
			return this.editMode && this.contactsTechnicalEditId === -1;
		}
	},
	methods: {
		...mapActions('integrators', [
			'setContactsVendor',
			'saveContactVendor',
			'deleteContactVendor'
		]),
		async fetchData() {
			this.loading = true;
			await this.setContactsVendor(this.vendorId)
				.then(() => {
					this.updateLocalContacts();
					this.vendorData = this.$store.state.integrators.integrator;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		businessEditMode(id) {
			this.editMode = true;
			this.contactsBusinessEditId = id;
		},
		supportEditMode(id) {
			this.editMode = true;
			this.contactsSupportEditId = id;
		},
		internalEditMode(id) {
			this.editMode = true;
			this.contactsInternalEditId = id;
		},
		technicalEditMode(id) {
			this.editMode = true;
			this.contactsTechnicalEditId = id;
		},
		closeVendorContactsEditFlyout() {
			if (!this.validateBeforeClose) {
				this.$emit('show', false);
			}
		},
		addContact(type) {
			this.editMode = true;

			switch (type) {
				case integratorsContactTypes.BUSINESS:
					this.contactsBusiness.push({
						id: 0
					});
					this.contactsBusinessEditId = 0;
					break;
				case integratorsContactTypes.SUPPORT:
					this.contactsSupport.push({
						id: 0
					});
					this.contactsSupportEditId = 0;
					break;
				case integratorsContactTypes.INTERNAL:
					this.contactsInternal.push({
						id: 0
					});
					this.contactsInternalEditId = 0;
					break;
				case integratorsContactTypes.TECHNICAL:
					this.contactsTechnical.push({
						id: 0
					});
					this.contactsTechnicalEditId = 0;
					break;
			}
		},
		cancelAction(contact, integratorsContactsTypeId) {
			this.editMode = false;

			if (contact.id === 0)
				switch (integratorsContactsTypeId) {
					case integratorsContactTypes.BUSINESS:
						this.contactsBusiness.pop();
						break;
					case integratorsContactTypes.SUPPORT:
						this.contactsSupport.pop();
						break;
					case integratorsContactTypes.INTERNAL:
						this.contactsInternal.pop();
						break;
					case integratorsContactTypes.TECHNICAL:
						this.contactsTechnical.pop();
						break;
				}

			this.resetEditIds();
		},
		async saveAction(
			contact,
			isPrimary,
			integratorsContactsTypeId,
			isPrimaryUpdate = false
		) {
			this.loading = true;

			const tempContact = {
				id: contact.id,
				integratorId: this.vendorData.id,
				integratorsContactsTypeId,
				name: contact.name,
				email: contact.email ? contact.email : '',
				title: contact.title ? contact.title : '',
				phone: contact.phone ? contact.phone : '',
				primary: isPrimary
			};

			await this.saveContactVendor({
				contact: tempContact,
				action: tempContact.id ? 'put' : 'post',
				isPrimaryUpdate
			})
				.then(() => {
					this.editMode = false;
					this.contactsBusinessEditId = -1;
					this.contactsSupportEditId = -1;
					this.contactsInternalEditId = -1;
					this.contactsTechnicalEditId = -1;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		async deleteAction(contacts, index, integratorsContactsTypeId) {
			this.showConfirmationModal = true;

			const modalResponse = await this.$refs.confirmationModal.confirm();
			if (!modalResponse) return;

			this.loading = true;
			if (
				!contacts[index].primary ||
				(contacts[index].primary && contacts.length === 1)
			) {
				await this.deleteContactVendor(contacts[index]).finally(() => {
					this.loading = false;
				});
			} else {
				// set a new primary
				await this.deleteContactVendor(contacts[index]);

				let newContactAsPrimary = {};

				switch (integratorsContactsTypeId) {
					case integratorsContactTypes.BUSINESS:
						if (this.contactsBusiness.length) {
							newContactAsPrimary = this.contactsBusiness[0];
						}
						break;
					case integratorsContactTypes.SUPPORT:
						if (this.contactsSupport.length) {
							newContactAsPrimary = this.contactsSupport[0];
						}
						break;
					case integratorsContactTypes.INTERNAL:
						if (this.contactsInternal.length) {
							newContactAsPrimary = this.contactsInternal[0];
						}
						break;
					case integratorsContactTypes.TECHNICAL:
						if (this.contactsTechnical.length) {
							newContactAsPrimary = this.contactsTechnical[0];
						}
						break;
				}

				this.saveAction(
					newContactAsPrimary,
					true,
					integratorsContactsTypeId,
					true
				);
			}
		},
		setNewPrimary(contacts, integratorsContactsTypeId) {
			if (contacts[0].primary === true) return;

			this.saveAction(contacts[0], true, integratorsContactsTypeId, true);
		},
		// helper functions
		resetEditIds() {
			this.contactsBusinessEditId = -1;
			this.contactsSupportEditId = -1;
			this.contactsInternalEditId = -1;
			this.contactsTechnicalEditId = -1;
		},
		updateLocalContacts() {
			if (
				this.contactsVendor.items &&
				this.contactsVendor.items.length > 0
			) {
				this.contactsBusiness = _.cloneDeep(
					this.contactsVendor.items[0].contacts
				);
				this.contactsSupport = _.cloneDeep(
					this.contactsVendor.items[1].contacts
				);
				this.contactsInternal = _.cloneDeep(
					this.contactsVendor.items[2].contacts
				);
				this.contactsTechnical = _.cloneDeep(
					this.contactsVendor.items[3].contacts
				);
			}

			this.putPrimaryAtPos0(this.contactsBusiness);
			this.putPrimaryAtPos0(this.contactsSupport);
			this.putPrimaryAtPos0(this.contactsInternal);
			this.putPrimaryAtPos0(this.contactsTechnical);
		},
		putPrimaryAtPos0(contactsList) {
			contactsList.sort((a, b) => {
				if (a.name < b.name) {
					return -1;
				}
				if (a.name > b.name) {
					return 1;
				}
				return 0;
			});

			for (let i = 0; i < contactsList.length; i++) {
				if (contactsList[i].primary === true) {
					let element = contactsList.splice(i, 1);
					contactsList.unshift(element[0]);
					break;
				}
			}
		},
		uniqueId() {
			return _.uniqueId();
		}
	},
	watch: {
		contactsVendor() {
			this.updateLocalContacts();
		},
		display: {
			handler(val) {
				if (val) {
					this.fetchData();
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
::v-deep .flyout__body {
	@include scrollbar;
}

.vendor-contact {
	&__spinner {
		@include vertical-horizontal-center-with-transform;
	}
}

::v-deep .flyout {
	@include mq($from: md) {
		width: 675px;
		max-width: initial;
	}

	@include mq($from: md-l) {
		width: 800px;
		max-width: initial;
	}

	@include mq($from: lg) {
		width: 1024px;
	}
}

.vendor-contact {
	display: flex;
	flex-direction: column;
	gap: $gutter-xl;

	&__text {
		@include heading('md');
	}

	&__draggable-list {
		margin-top: $gutter-lg;
	}

	&__help-text {
		@include body-text('regular');
		margin-top: $gutter-md;
	}

	&__add {
		margin-top: $gutter-lg;
	}
}
</style>
